import React, { useState } from 'react'
import { DateRangePicker } from 'rsuite'
import { BlobProvider } from '@react-pdf/renderer'
import Spread from './PDF/Spread'
import axios from 'axios'
import { getSkuLocation } from 'src/services'
import * as XLSX from 'xlsx'

function AgentReport() {
  const [dateRange, setDateRange] = useState(null)
  const [error, setError] = useState('')
  const [loadingDate, setLoadingDate] = useState(false)
  const [pdfData, setPDFDate] = useState(null)
  const [map_data, setMapData] = useState({})
  const [additionalLineItems, setAdditionalLineItems] = useState([])

  const findProduct = (id) => map_data.product?.find((x) => x.id === Number(id))

  const findMix = (orderId, productId) => {
    return (
      map_data.order_list_products?.find(
        (x) => x.order_id === Number(orderId) && x.product_mix_id === findProduct(productId)?.id,
      ) || null
    )
  }

  const getProductLabel = (dtRow, productId) => {
    if (productId) {
      const p = findProduct(productId)
      if (p) return p.product_name || p.product_docket || String(productId)
    }

    let list = null
    if (Array.isArray(dtRow?.order_list_products) && dtRow.order_list_products.length) {
      list = dtRow.order_list_products
    } else if (Array.isArray(dtRow?.order_list) && dtRow.order_list.length) {
      list = dtRow.order_list.flatMap((ol) => ol.order_list_products || [])
    }

    if (Array.isArray(list) && list.length) {
      if (productId) {
        const found = list.find(
          (it) =>
            Number(it.product_mix_id) === Number(productId) ||
            String(it.product_mix_id) === String(productId),
        )
        if (found && (found.product_name || found.product_docket))
          return found.product_name || found.product_docket
      }

      for (const it of list) {
        if (it && (it.product_name || it.product_docket)) {
          return it.product_name || it.product_docket
        }
      }
    }

    if (dtRow?.product?.product_name) return dtRow.product.product_name
    if (dtRow?.product_name) return dtRow.product_name
    if (dtRow?.product_docket) return dtRow.product_docket
    if (typeof productId === 'string' || typeof productId === 'number') return String(productId)
    return ''
  }

  const computeRowTonnes = (dtRow, product) => {
    let mixPercentage = null

    if (Array.isArray(dtRow.order_list_products) && dtRow.order_list_products.length) {
      const m = dtRow.order_list_products.find(
        (x) =>
          Number(x.product_mix_id) === Number(product) ||
          String(x.product_mix_id) === String(product),
      )
      if (m) mixPercentage = m.mix_percentage ?? m.mixPercentage ?? m.mix_percentage
    }

    if (mixPercentage === null) {
      const found = findMix(dtRow.id, product)
      mixPercentage = found ? found.mix_percentage : null
    }

    if (mixPercentage !== null && Number(mixPercentage) !== 0) {
      return (Number(mixPercentage) / 100) * Number(dtRow.tons_ordered || dtRow.quantity || 0)
    }

    return Number(
      dtRow.tons_ordered ?? dtRow.tons_ordered_total ?? dtRow.tons ?? dtRow.quantity ?? 0,
    )
  }

  const computeRowTonnesActual = (dtRow, product) => {
    let mixPercentage = null

    if (Array.isArray(dtRow.order_list_products) && dtRow.order_list_products.length) {
      const m = dtRow.order_list_products.find(
        (x) =>
          Number(x.product_mix_id) === Number(product) ||
          String(x.product_mix_id) === String(product),
      )
      if (m) mixPercentage = m.mix_percentage ?? m.mixPercentage ?? m.mix_percentage
    }

    if (mixPercentage === null) {
      const found = findMix(dtRow.id, product)
      mixPercentage = found ? found.mix_percentage : null
    }

    if (mixPercentage !== null && Number(mixPercentage) !== 0) {
      return (Number(mixPercentage) / 100) * Number(dtRow.tons_actual || dtRow.tons_complete || 0)
    }

    return Number(dtRow.tons_actual ?? dtRow.tons_complete ?? dtRow.tons ?? 0)
  }

  const computeRowCompleted = (dtRow, product) => {
    let mixPercentage = null

    if (Array.isArray(dtRow.order_list_products) && dtRow.order_list_products.length) {
      const m = dtRow.order_list_products.find(
        (x) =>
          Number(x.product_mix_id) === Number(product) ||
          String(x.product_mix_id) === String(product),
      )
      if (m) mixPercentage = m.mix_percentage ?? m.mixPercentage ?? m.mix_percentage
    }

    if (mixPercentage === null) {
      const found = findMix(dtRow.id, product)
      mixPercentage = found ? found.mix_percentage : null
    }

    if (mixPercentage !== null && Number(mixPercentage) !== 0) {
      if (Number(dtRow.order_status) === 1) {
        return (Number(mixPercentage) / 100) * Number(dtRow.tons_actual || dtRow.tons_complete || 0)
      }
      return (Number(mixPercentage) / 100) * Number(dtRow.tons_ordered || 0)
    }

    return Number(
      Number(dtRow.order_status) === 1
        ? dtRow.tons_actual ?? dtRow.tons_complete ?? 0
        : dtRow.tons_ordered ?? dtRow.tons ?? 0,
    )
  }

  const getDataReport = (data) => {
    setDateRange(data)
    setLoadingDate(true)

    if (!data) {
      setError(null)
      setPDFDate(null)
      setAdditionalLineItems([])
      setLoadingDate(false)
      return
    }

    axios
      .post(`/api/agent_report`, { data })
      .then((res) => {
        const ordersData = res.data.orders || res.data.spreadreport || {}

        if (!ordersData || Object.keys(ordersData).length === 0) {
          setError('No data found')
          setAdditionalLineItems([])
        } else {
          setError(null)
          setMapData({
            dzone: res.data.dzone,
            otype: res.data.otype,
            machine: res.data.machine,
            product: res.data.product,
            order_list_products: res.data.order_list_products,
            sku_location:
              res.data.sku_location || res.data.skulocation || res.data.skuLocation || [],
          })

          setPDFDate(ordersData)

          const orderIds = new Set()
          const traverseCollectIds = (node) => {
            if (!node) return
            if (Array.isArray(node)) {
              node.forEach((o) => {
                if (o && o.id) orderIds.add(Number(o.id))
              })
            } else if (typeof node === 'object') {
              Object.values(node).forEach((v) => traverseCollectIds(v))
            }
          }

          traverseCollectIds(ordersData)

          let collected = []
          if (res.data.orders) {
            const traverse = (node) => {
              if (!node) return
              if (Array.isArray(node)) {
                node.forEach((o) => {
                  if (!o || !o.id) return
                  const items = Array.isArray(o.other_line_items)
                    ? o.other_line_items
                    : Array.isArray(o.line_items)
                    ? o.line_items
                    : null
                  if (items && orderIds.has(Number(o.id))) {
                    collected.push(...items)
                  }
                })
              } else if (typeof node === 'object') {
                Object.values(node).forEach((v) => traverse(v))
              }
            }

            traverse(res.data.orders)
          }

          if (collected.length > 0) setAdditionalLineItems(collected)
          else setAdditionalLineItems([])
        }

        setLoadingDate(false)
      })
      .catch(() => {
        setError('Error fetching report data')
        setAdditionalLineItems([])
        setLoadingDate(false)
      })
  }

  const formatDate = (date) => {
    if (!date) return ''
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return ''

    let month = '' + (d.getMonth() + 1)
    let day = '' + d.getDate()
    const year = d.getFullYear()

    if (month.length < 2) month = '0' + month
    if (day.length < 2) day = '0' + day

    return [year, month, day].join('-')
  }

  const formatDateRange = (range) => {
    if (!range || !Array.isArray(range)) return ''
    const [start, end] = range
    return `${formatDate(start)} to ${formatDate(end)}`
  }

  const downloadExcel = async () => {
    if (!pdfData) return

    let skuList = map_data.sku_location || map_data.skulocation || map_data.skuLocation || []

    if (!Array.isArray(skuList) || skuList.length === 0) {
      try {
        const resp = await getSkuLocation()
        skuList =
          resp?.data?.sku_location ||
          resp?.data?.skulocation ||
          resp?.data?.skuLocation ||
          resp?.data ||
          []

        setMapData((prev) => ({ ...prev, sku_location: skuList }))
      } catch (e) {
        console.warn('getSkuLocation() failed', e)
        skuList = []
      }
    }

    const combined = []
    if (Array.isArray(additionalLineItems) && additionalLineItems.length) {
      combined.push(...additionalLineItems)
    }

    const collectFromOrder = (order) => {
      if (!order) return
      const items = Array.isArray(order.other_line_items)
        ? order.other_line_items
        : Array.isArray(order.line_items)
        ? order.line_items
        : null

      if (items && items.length) {
        items.forEach((it) => {
          if (!it) return
          const newItem = { ...it }
          if (newItem.order_id === undefined || newItem.order_id === null) {
            newItem.order_id = order.id ?? order.order_id ?? order.order
          }
          combined.push(newItem)
        })
      }
    }

    const walk = (node) => {
      if (!node) return
      if (Array.isArray(node)) return node.forEach(collectFromOrder)
      if (typeof node === 'object') {
        Object.values(node).forEach((v) => walk(v))
      }
    }

    walk(pdfData)

    const headers = [
      'Agent',
      'Order ID',
      'Product',
      'Customer Name',
      'Spread Date',
      'Order Date',
      'Tonnes Ordered',
      'Tonnes Actual',
      'Predicted',
    ]

    const aoa = []
    aoa.push(headers)

    let grandOrdered = 0
    let grandActual = 0
    let grandPred = 0

    const subtotalRows = []
    const zoneHeaderRows = []
    let currentRow = 1

    const isAgentGrouped =
      Object.keys(pdfData).length > 0 && Object.values(pdfData).every((v) => typeof v === 'object')

    const orphanRows = []
    const writtenRows = new Set()

    if (isAgentGrouped) {
      Object.keys(pdfData).forEach((agentId) => {
        const agentBlock = pdfData[agentId]
        if (!agentBlock) return

        const findAgentName = (node) => {
          if (!node) return null

          if (node.agent) {
            if (typeof node.agent === 'string' && node.agent.trim()) return node.agent
            if (node.agent?.name) return node.agent.name
          }

          if (node.agent_name) return node.agent_name

          if (Array.isArray(node)) {
            for (const o of node) {
              if (!o) continue
              if (o.agent?.name) return o.agent.name
              if (o.agent_name) return o.agent_name
              if (typeof o.agent === 'string' && o.agent.trim()) return o.agent
            }
            return null
          }

          if (typeof node === 'object') {
            for (const v of Object.values(node)) {
              const r = findAgentName(v)
              if (r) return r
            }
          }

          return null
        }

        const agentName = findAgentName(agentBlock)

        if (agentName) {
          aoa.push([agentName, '', '', '', '', '', '', '', ''])
          zoneHeaderRows.push(currentRow)
          currentRow += 1
        }

        Object.keys(agentBlock || {}).forEach((zoneKey) => {
          ;(agentBlock[zoneKey] || []).forEach((dtRow) => {
            const orderId = dtRow.id ?? dtRow.order_id ?? dtRow.order ?? ''
            if (!orderId) return

            const productLabel = getProductLabel(dtRow, dtRow.product_id)
            const rowAgentName = dtRow?.agent?.name || dtRow?.agent_name || dtRow?.agent || null

            const ordered = computeRowTonnes(dtRow, dtRow.product_id)
            const actual = computeRowTonnesActual(dtRow, dtRow.product_id)
            const pred = computeRowCompleted(dtRow, dtRow.product_id)

            const row = [
              '',
              orderId,
              productLabel,
              dtRow.customer?.account_name || '',
              dtRow.tons_complete_time || '',
              formatDate(dtRow.created_at),
              Number(Number(ordered).toFixed(3)),
              Number(Number(actual).toFixed(3)),
              Number(Number(pred).toFixed(3)),
            ]

            const rowKey = [
              orderId,
              productLabel,
              dtRow.customer?.account_name || '',
              dtRow.tons_complete_time || '',
              formatDate(dtRow.created_at),
            ].join('||')

            if (writtenRows.has(rowKey)) return
            writtenRows.add(rowKey)

            grandOrdered += ordered
            grandActual += actual
            grandPred += pred

            if (!rowAgentName) {
              orphanRows.push(row)
            } else {
              aoa.push(row)
              currentRow += 1
            }
          })
        })
      })
    } else {
      Object.keys(pdfData).forEach((zoneId) => {
        const zoneName =
          map_data.dzone?.find((z) => z.id === Number(zoneId))?.name || zoneId || 'No Zone'
        aoa.push([zoneName, '', '', '', '', '', '', '', ''])
        zoneHeaderRows.push(currentRow)
        currentRow += 1

        Object.keys(pdfData[zoneId]).forEach((productId) => {
          const productLabel = getProductLabel(
            (pdfData[zoneId][productId] || [])?.[0] || {},
            productId,
          )
          const list = pdfData[zoneId][productId]

          let subtotalOrdered = 0
          let subtotalActual = 0
          let subtotalPred = 0

          list.forEach((dtRow) => {
            const orderId = dtRow.id ?? dtRow.order_id ?? dtRow.order ?? ''
            if (!orderId) return

            const ordered = computeRowTonnes(dtRow, productId)
            const actual = computeRowTonnesActual(dtRow, productId)
            const pred = computeRowCompleted(dtRow, productId)

            const rowKey = [
              orderId,
              productLabel,
              dtRow.customer?.account_name || '',
              dtRow.tons_complete_time || '',
              formatDate(dtRow.created_at),
            ].join('||')

            if (writtenRows.has(rowKey)) return
            writtenRows.add(rowKey)

            subtotalOrdered += ordered
            subtotalActual += actual
            subtotalPred += pred

            grandOrdered += ordered
            grandActual += actual
            grandPred += pred

            aoa.push([
              '',
              orderId,
              productLabel,
              dtRow.customer?.account_name || '',
              dtRow.tons_complete_time || '',
              formatDate(dtRow.created_at),
              Number(Number(ordered).toFixed(3)),
              Number(Number(actual).toFixed(3)),
              Number(Number(pred).toFixed(3)),
            ])
            currentRow += 1
          })

          const subtotalIndex = currentRow
          aoa.push([
            '',
            '',
            `${productLabel} Subtotal`,
            '',
            '',
            '',
            Number(Number(subtotalOrdered).toFixed(3)),
            Number(Number(subtotalActual).toFixed(3)),
            Number(Number(subtotalPred).toFixed(3)),
          ])
          subtotalRows.push(subtotalIndex)
          currentRow += 1
        })
      })
    }

    if (orphanRows.length > 0) {
      const orphanHeaderIndex = aoa.length
      aoa.push(['No agents', '', '', '', '', '', '', '', ''])
      zoneHeaderRows.push(orphanHeaderIndex)

      orphanRows.forEach((r) => {
        aoa.push(r)
      })
    }

    const grandIndex = aoa.length
    aoa.push([
      '',
      '',
      'GRAND TOTAL',
      '',
      '',
      '',
      Number(Number(grandOrdered).toFixed(3)),
      Number(Number(grandActual).toFixed(3)),
      Number(Number(grandPred).toFixed(3)),
    ])

    const ws = XLSX.utils.aoa_to_sheet(aoa)

    ws['!cols'] = [
      { wpx: 150 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 300 },
      { wpx: 120 },
      { wpx: 120 },
      { wpx: 120 },
      { wpx: 120 },
      { wpx: 120 },
    ]

    aoa[0].forEach((_, c) => {
      const addr = XLSX.utils.encode_cell({ r: 0, c })
      if (!ws[addr]) ws[addr] = { t: 's', v: aoa[0][c] }
      ws[addr].s = { font: { bold: true } }
    })

    ws['!merges'] = zoneHeaderRows.map((r) => ({
      s: { r, c: 0 },
      e: { r, c: headers.length - 1 },
    }))

    zoneHeaderRows.forEach((r) => {
      const addr = XLSX.utils.encode_cell({ r, c: 0 })
      if (!ws[addr]) ws[addr] = { t: 's', v: '' }
      ws[addr].s = { font: { bold: true } }
    })

    subtotalRows.forEach((r) => {
      for (let c = 0; c < headers.length; c++) {
        const addr = XLSX.utils.encode_cell({ r, c })
        if (ws[addr]) {
          ws[addr].s = {
            font: { bold: true },
            alignment: { horizontal: 'right' },
          }
        }
      }
    })

    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: grandIndex, c })
      if (ws[addr]) {
        ws[addr].s = {
          font: { bold: true },
          alignment: { horizontal: 'right' },
        }
      }
    }

    const numericCols = [6, 7, 8]
    for (let r = 1; r <= aoa.length - 1; r++) {
      numericCols.forEach((c) => {
        const addr = XLSX.utils.encode_cell({ r, c })
        if (ws[addr]) {
          ws[addr].t = 'n'
          ws[addr].s = {
            ...(ws[addr].s || {}),
            alignment: { horizontal: 'right' },
          }
        }
      })
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Agent Report')

    const seen = new Set()
    const deduped = []
    combined.forEach((it) => {
      if (!it) return
      const orderId = it.order_id ?? it.orderId ?? it.order ?? ''
      const description = it.description ?? it.desc ?? it.name ?? ''
      const quantity = Number(it.quantity ?? it.qty ?? it.Qty ?? 0)
      const amount = Number(it.amount ?? it.Amount ?? 0)
      const key = `${orderId}||${description}||${quantity}||${amount}`

      if (!seen.has(key)) {
        seen.add(key)
        deduped.push({
          ...it,
          quantity,
          amount,
          order_id: orderId,
        })
      }
    })

    if (deduped.length > 0) {
      const headers2 = [
        'Order ID',
        'Line Item Description',
        'Location',
        'Division',
        'Quantity',
        'Amount',
        'Total',
      ]

      const aoa2 = [headers2]

      deduped.forEach((it) => {
        const orderId = it.order_id ?? it.orderId ?? it.order ?? ''
        const description = it.description ?? it.desc ?? it.name ?? ''

        let locationVal = it.location ?? it.sku_location ?? it.location_name ?? ''

        if (
          locationVal !== '' &&
          (typeof locationVal === 'number' || /^\d+$/.test(String(locationVal)))
        ) {
          const lid = Number(locationVal)
          const foundLoc = Array.isArray(skuList) ? skuList.find((l) => Number(l.id) === lid) : null
          locationVal = foundLoc?.name || String(lid)
        }

        let divisionVal = it.division ?? it.branch ?? it.division_name ?? ''
        if (
          divisionVal !== '' &&
          (typeof divisionVal === 'number' || /^\d+$/.test(String(divisionVal)))
        ) {
          const did = Number(divisionVal)
          divisionVal =
            did === 1 ? 'Spreading' : did === 2 ? 'Transport' : did === 3 ? 'Mining' : String(did)
        }

        const quantity = Number(it.quantity ?? it.qty ?? it.Qty ?? 0)
        const amount = Number(it.amount ?? it.Amount ?? 0)
        const total = Number(it.total ?? it.Total ?? quantity * amount)

        aoa2.push([orderId, description, locationVal, divisionVal, quantity, amount, total])
      })

      const ws2 = XLSX.utils.aoa_to_sheet(aoa2)

      headers2.forEach((_, c) => {
        const addr = XLSX.utils.encode_cell({ r: 0, c })
        if (ws2[addr]) ws2[addr].s = { font: { bold: true } }
      })

      ws2['!cols'] = [
        { wpx: 100 },
        { wpx: 400 },
        { wpx: 200 },
        { wpx: 150 },
        { wpx: 100 },
        { wpx: 120 },
        { wpx: 140 },
      ]

      XLSX.utils.book_append_sheet(wb, ws2, 'Additional Line Items')
    }

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true })
    const blob = new Blob([wbout], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `agent_report_${formatDateRange(dateRange)}.xlsx`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <h1 className="mb-4">Agent Report</h1>
      <div className="d-flex align-items-end">
        <div className="d-flex flex-column ">
          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            style={{ width: 230 }}
            onChange={(e) => setDateRange(e)}
            value={dateRange}
          />
        </div>

        <div className="mx-2">
          <button
            type="button"
            className="btn btn-block btn-outline-primary btn-sm"
            onClick={() => getDataReport(dateRange)}
          >
            Search
          </button>
        </div>

        <div className="mx-3">
          {error ? (
            <div className="btn btn-block btn-outline-info btn-sm">{error}</div>
          ) : dateRange === null ? null : loadingDate ? (
            <div className="btn btn-block btn-outline-info btn-sm">Loading....</div>
          ) : pdfData && !loadingDate ? (
            <>
              <div style={{ display: 'inline-block', marginLeft: 8 }}>
                <button
                  type="button"
                  className="btn btn-block btn-outline-success btn-sm"
                  onClick={downloadExcel}
                >
                  Download Excel
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default AgentReport
